import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, filter } from 'rxjs';

import { WebsocketService } from '@core/services/websocket.service';
import { CONFIG_TOKEN } from '@core/tokens';

import { ChargingTimelineResponse, ChargingTimelineWebsocketMessage } from '../charger-details/charger-details.models';
import { GraphResponse, MeteringMetric } from '../graphs.models';
import {
  AssetOverviewResponse,
  AssetOverviewWebsocketMessage,
  ChargingOverviewResponse,
  ChargingOverviewWebsocketMessage,
  CreateTransitRouteRequest,
  EvseDetailsResponse,
  EvseDetailsWebsocketMessage,
  EvseOverviewResponse,
  EvseOverviewWebsocketMessage,
  InfrastructureOverviewResponse,
  InfrastructureOverviewWebsocketMessage,
  MapOverviewResponse,
  MapOverviewWebsocketMessage,
  OnTheRoadVehiclesResponse,
  OnTheRoadVehiclesWebsocketMessage,
  TransitRoutesResponse,
  UpdateTransitRouteRequest,
  VehicleListRequest,
  VehicleListResponse,
  VehicleOverviewResponse,
  VehicleOverviewWebsocketMessage,
  VehiclesFiltersResponse,
  VehiclesFiltersWebsocketMessage,
  VehiclesWebsocketMessage,
} from './single-site.models';

@Injectable({
  providedIn: 'root',
})
export class SingleSiteService {
  readonly #ROOT_URL = inject(CONFIG_TOKEN).backendRootUrl;

  readonly #httpClient = inject(HttpClient);
  readonly #websocketService = inject(WebsocketService);

  public getEvseOverview$(siteId: string): Observable<EvseOverviewResponse> {
    return this.#httpClient.get<EvseOverviewResponse>(`${this.#ROOT_URL}/api/v1/sites/${siteId}/evse-overview`);
  }

  public getEvseOverviewWebsocketMessage$(siteId: string): Observable<EvseOverviewWebsocketMessage> {
    return this.#websocketService
      .connectToTopic$<EvseOverviewWebsocketMessage>(`sites/${siteId}/evse-overview`)
      .pipe(filter((message) => message.type === 'evse-overview'));
  }

  public getVehicleOverview$(siteId: string): Observable<VehicleOverviewResponse> {
    return this.#httpClient.get<VehicleOverviewResponse>(`${this.#ROOT_URL}/api/v1/sites/${siteId}/vehicle-overview`);
  }

  public getVehicleOverviewWebsocketMessage$(siteId: string): Observable<VehicleOverviewWebsocketMessage> {
    return this.#websocketService
      .connectToTopic$<VehicleOverviewWebsocketMessage>(`sites/${siteId}/vehicle-overview`)
      .pipe(filter((message) => message.type === 'vehicle-overview'));
  }

  public getMapOverview$(siteId: string): Observable<MapOverviewResponse> {
    return this.#httpClient.get<MapOverviewResponse>(`${this.#ROOT_URL}/api/v1/sites/${siteId}/map-overview`);
  }

  public getMapOverviewWebsocketMessage$(siteId: string): Observable<MapOverviewWebsocketMessage> {
    return this.#websocketService
      .connectToTopic$<MapOverviewWebsocketMessage>(`sites/${siteId}/map-overview`)
      .pipe(filter((message) => message.type === 'map-overview'));
  }

  public getOnTheRoadVehicles$(siteId: string): Observable<OnTheRoadVehiclesResponse> {
    return this.#httpClient.get<OnTheRoadVehiclesResponse>(`${this.#ROOT_URL}/api/v1/sites/${siteId}/on-the-road-vehicles`);
  }

  public getOnTheRoadVehiclesWebsocketMessage$(siteId: string): Observable<OnTheRoadVehiclesWebsocketMessage> {
    return this.#websocketService
      .connectToTopic$<OnTheRoadVehiclesWebsocketMessage>(`sites/${siteId}/on-the-road-vehicles`)
      .pipe(filter((message) => message.type === 'on-the-road-vehicles'));
  }

  public getMapDetails$(siteId: string, evseId: string): Observable<EvseDetailsResponse> {
    return this.#httpClient.get<EvseDetailsResponse>(`${this.#ROOT_URL}/api/v1/sites/${siteId}/map-details/evse/${evseId}`);
  }

  public getMapDetailsWebsocketMessage$(siteId: string, evseId: string): Observable<EvseDetailsWebsocketMessage> {
    return this.#websocketService
      .connectToTopic$<EvseDetailsWebsocketMessage>(`sites/${siteId}/map-details/evse/${evseId}`)
      .pipe(filter((message) => message.type === 'map-details-evse'));
  }

  public getMapDetailsEvseActivityTimeline$(evseId: string, limit: number, beforeTime?: string): Observable<ChargingTimelineResponse> {
    let queryParams: HttpParams = new HttpParams().set('limit', limit);

    if (beforeTime) {
      queryParams = queryParams.set('before', beforeTime);
    }

    return this.#httpClient.get<ChargingTimelineResponse>(`${this.#ROOT_URL}/api/v1/evse/${evseId}/activity/timeline`, {
      params: queryParams,
    });
  }

  public getMapDetailsEvseActivityTimelineWebsocketMessage$(evseId: string): Observable<ChargingTimelineWebsocketMessage> {
    const topic = `evse/${evseId}/activity/timeline`;
    return this.#websocketService
      .connectToTopic$<ChargingTimelineWebsocketMessage>(topic)
      .pipe(filter((message) => message.type === 'charging-timeline' && message.topic === topic));
  }

  public getChargingOverview$(siteId: string): Observable<ChargingOverviewResponse> {
    return this.#httpClient.get<ChargingOverviewResponse>(`${this.#ROOT_URL}/api/v1/sites/${siteId}/charging-overview`);
  }

  public getChargingOverviewWebsocketMessage$(siteId: string): Observable<ChargingOverviewWebsocketMessage> {
    return this.#websocketService
      .connectToTopic$<ChargingOverviewWebsocketMessage>(`sites/${siteId}/charging-overview`)
      .pipe(filter((message) => message.type === 'charging-overview'));
  }

  public getInfrastructureOverview$(siteId: string): Observable<InfrastructureOverviewResponse> {
    return this.#httpClient.get<InfrastructureOverviewResponse>(`${this.#ROOT_URL}/api/v1/sites/${siteId}/infrastructure-overview`);
  }

  public getInfrastructureOverviewWebsocketMessage$(siteId: string): Observable<InfrastructureOverviewWebsocketMessage> {
    return this.#websocketService
      .connectToTopic$<InfrastructureOverviewWebsocketMessage>(`sites/${siteId}/infrastructure-overview`)
      .pipe(filter((message) => message.type === 'infrastructure-overview'));
  }

  public getAssetOverview$(siteId: string): Observable<AssetOverviewResponse> {
    return this.#httpClient.get<AssetOverviewResponse>(`${this.#ROOT_URL}/api/v1/sites/${siteId}/asset-overview`);
  }

  public getAssetOverviewWebsocketMessage$(siteId: string): Observable<AssetOverviewWebsocketMessage> {
    return this.#websocketService
      .connectToTopic$<AssetOverviewWebsocketMessage>(`sites/${siteId}/asset-overview`)
      .pipe(filter((message) => message.type === 'asset-overview'));
  }

  public getVehiclesFilters$(siteId: string): Observable<VehiclesFiltersResponse> {
    return this.#httpClient.get<VehiclesFiltersResponse>(`${this.#ROOT_URL}/api/v1/sites/${siteId}/vehicles-filters`);
  }

  public getVehiclesFiltersWebsocketMessage$(siteId: string): Observable<VehiclesFiltersWebsocketMessage> {
    return this.#websocketService
      .connectToTopic$<VehiclesFiltersWebsocketMessage>(`sites/${siteId}/vehicles-filters`)
      .pipe(filter((message) => message.type === 'vehicles-filters'));
  }

  public getVehiclesForSites$(siteIds: string[], request: VehicleListRequest): Observable<VehicleListResponse> {
    const httpParams = new HttpParams()
      .append('limit', request.limit.toString())
      .append('offset', request.offset.toString())
      .append('site', siteIds.join(','));

    return this.#httpClient.get<VehicleListResponse>(`${this.#ROOT_URL}/api/v1/vehicle-list`, {
      params: httpParams,
    });
  }

  public getVehiclesWebsocketMessage$(siteId: string): Observable<VehiclesWebsocketMessage> {
    return this.#websocketService
      .connectToTopic$<VehiclesWebsocketMessage>(`sites/${siteId}/vehicle-list`)
      .pipe(filter((message) => message.type === 'vehicles'));
  }

  public getTransitRoutes$(siteId: string): Observable<TransitRoutesResponse> {
    return this.#httpClient.get<TransitRoutesResponse>(`${this.#ROOT_URL}/api/v1/sites/${siteId}/transit-routes`);
  }

  public createTransitRoute$(siteId: string, request: CreateTransitRouteRequest): Observable<void> {
    return this.#httpClient.post<void>(`${this.#ROOT_URL}/api/v1/sites/${siteId}/transit-routes`, request);
  }

  public updateTransitRoute$(siteId: string, transitRouteId: string, request: UpdateTransitRouteRequest): Observable<void> {
    return this.#httpClient.put<void>(`${this.#ROOT_URL}/api/v1/sites/${siteId}/transit-routes/${transitRouteId}`, request);
  }

  public deleteTransitRoute$(siteId: string, transitRouteId: string): Observable<void> {
    return this.#httpClient.delete<void>(`${this.#ROOT_URL}/api/v1/sites/${siteId}/transit-routes/${transitRouteId}`);
  }

  public triggerLoadProfile$(siteId: string): Observable<void> {
    return this.#httpClient.post<void>(`${this.#ROOT_URL}/api/v1/sites/${siteId}/load-profile/trigger`, null);
  }

  public getGraphsData$(
    siteId: string,
    assetIds: string[],
    start: string,
    end: string,
    metric1: MeteringMetric,
    metric2?: MeteringMetric,
    granularity?: string
  ): Observable<GraphResponse> {
    let httpParams = new HttpParams().appendAll({
      asset: assetIds,
      metric1: metric1,
      start: start,
      end: end,
    });

    if (metric2) {
      httpParams = httpParams.append('metric2', metric2);
    }

    if (granularity) {
      httpParams = httpParams.append('granularity', granularity);
    }

    return this.#httpClient.get<GraphResponse>(`${this.#ROOT_URL}/api/v1/sites/${siteId}/graph`, {
      params: httpParams,
    });
  }
}
